import React, { useEffect, useState } from 'react';
import './WorkWithRadialChart.scss';
import { getProjectsSimpleTableIndicator } from '../../../redux/actions/indicatorProjects';
import { useDispatch, useSelector } from 'react-redux';
import Indicator from '../../../models/Indicator';
import Project from '../../../models/Project';
import NumberFormat from 'react-number-format';
import { useTranslation } from 'react-i18next';
import { IndicatorKeyEnum as key } from '../../../models/IndicatorKeyEnum';
import Select from 'react-select';
import AverageLagTable from '../AverageLagTable/AverageLagTable';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import IndicatorDetailFilter from '../../../models/IndicatorDetailFilter';
import { handleLoading } from '../../utils';
import FilterIndicators from '../../../models/FilterIndicators';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { translateMultipleText } from '../../../services/stringUtil';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import { resetIndicatorDetailFilter } from '../../../redux/actions/indicatorDetailFilters';
import Filter from '../../Portfolio/Filter/Filter';
import { ProjectsListSkeleton } from '../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';

interface ReceivedProps {
  currentIndicator: Indicator;
  circleData?: any;
}

type Props = ReceivedProps;
export const WorkWithRadialChart = (props: Props) => {
  const { currentIndicator, circleData } = props;
  const { value, label } = circleData;
  const { t } = useTranslation();
  const color = 'rgba(102, 102, 102, 1)';
  const stateOptions = [
    { value: 'totalData', label: t('all'), color },
    { value: 'criticalProductsData', label: t('critical_products'), color },
    { value: 'activitiesData', label: t('activities'), color },
  ];
  const [optionValue, setOptionValue] = React.useState<any>(stateOptions[0]);
  const [loadingData, setLoadingData] = React.useState<boolean>(false);
  const { projectTypeFilterOnDetail } = useSelector<any, IndicatorDetailFilter>(state => state.indicatorDetailFilter);
  const changeOption = data => {
    let newCenterText: string;

    if (data.value === 'totalData') {
      newCenterText = currentIndicator.data.centerTextTotal;
    } else if (data.value === 'criticalProductsData') {
      newCenterText = currentIndicator.data.centerTextProducts;
    } else {
      newCenterText = currentIndicator.data.centerTextActivities;
    }

    const newIndicator = {
      ...currentIndicator,
      data: { ...currentIndicator.data, circles: currentIndicator.data[data.value], centerText: newCenterText },
    };
    dispatch(setCurrentIndicator(newIndicator));
    setOptionValue(data);
  };

  const dispatch = useDispatch();
  const indicatorProjects = useSelector<any, Project[]>(state => state.indicatorProjects);

  // Filter
  const filterIndicators = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = useState(false);
  const [totalProjectsData, setTotalProjectsData] = useState(false);
  const [flag, setFlag] = useState<boolean>(false);

  const projectTypeFilterChange = {
    ...filterIndicators,
    common: { ...filterIndicators.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async () => {
    dispatch(setLoading(true));
    setIsLoading(true);

    try {
      const indicatorsToProcess = await getNewFilteredIndicators(
        {},
        { projectProductTypes: stateProjectProductTypes },
        {
          ...projectTypeFilterChange.common,
        },
      );
      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicators.common.projectTypeFilters,
      );
      setTotalProjectsData(emptyProjects(indicatorsToProcess.totalProjects));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      dispatch(setLoading(false));
      setIsLoading(false);
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan]) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'AVERAGE_LAG')));
    setIsLoading(false);
  };

  useEffect(() => {
    getFilteredSimpleIndicators(dispatch, currentIndicator.id, setLoadingData, projectTypeFilterChange);
  }, [currentIndicator.id, dispatch, projectTypeFilterOnDetail, filterIndicators.common]);

  useEffect(() => {
    if (
      filterIndicators.common.coReponsibleDivisions.length > 0 ||
      filterIndicators.common.countryId.length > 0 ||
      filterIndicators.common.divisionId.length > 0 ||
      filterIndicators.common.interestIssues.length > 0 ||
      filterIndicators.common.participantDivisions.length > 0 ||
      filterIndicators.common.regionId.length > 0 ||
      filterIndicators.common.sectors.length > 0 ||
      filterIndicators.common.teamLeaders.length > 0
    )
      handleCommonFilterChange();
    setFlag(true);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  useEffect(() => {
    dispatch(resetIndicatorDetailFilter());
    if (
      !menuTabs.firstLoad &&
      // tabId0OrCreatingEditing(menuTabs, creatingOrEditing) &&
      filterIndicators.common.coReponsibleDivisions.length === 0 &&
      filterIndicators.common.countryId.length === 0 &&
      filterIndicators.common.divisionId.length === 0 &&
      filterIndicators.common.interestIssues.length === 0 &&
      filterIndicators.common.participantDivisions.length === 0 &&
      filterIndicators.common.regionId.length === 0 &&
      filterIndicators.common.sectors.length === 0 &&
      filterIndicators.common.teamLeaders.length === 0 &&
      flag
    ) {
      handleCommonFilterChange();
      setFlag(false);
      if (creatingOrEditing) {
        dispatch(updateCreatingOrEditing(false));
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicators.common]);

  return (
    <>
      <div
        className={`work-with-radial-chart-container  ${indicatorProjects.length === 0 ? '' : 'adjust-height'}`}
        style={currentIndicator.indicatorKey === key.AVERAGE_LAG ? { paddingTop: 0 } : {}}
      >
        {currentIndicator.indicatorKey !== key.AVERAGE_LAG && (
          <>
            <div className="title">{t(label)}</div>

            <div className="description">
              <NumberFormat
                value={value.toFixed(0)}
                displayType={'text'}
                thousandSeparator={'.'}
                decimalSeparator={','}
                prefix={`${t('dolar_sign')} `}
              />
            </div>
          </>
        )}

        <div
          className="table-container"
          style={currentIndicator.indicatorKey === key.AVERAGE_LAG ? { backgroundColor: 'white' } : {}}
        >
          {!isLoading ? <AverageLagTable projects={indicatorProjects} loading={isLoading} /> : <ProjectsListSkeleton />}

          {/* <ProjectCounter
          indicatorKey={key.AVERAGE_LAG}
          arrayToCount={indicatorProjects}
          countProjects={true}
          footerColumnsInformation={{
            columnData1: sumValueByField('field1', indicatorProjects),
            columnData3: sumValueByField('field3', indicatorProjects),
            columnData4: sumValueByField('field4', indicatorProjects),
            columnData2: sumValueByField('field2', indicatorProjects),
            arrayToCount: indicatorProjects,
            countProjects: true,
          }}
        /> */}
        </div>
      </div>
    </>
  );
};

export const getFilteredSimpleIndicators = (d, currentIndicatorId, setLoading, projectTypeFilterChange) => {
  return handleLoading(
    () => d(getProjectsSimpleTableIndicator(currentIndicatorId, undefined, projectTypeFilterChange)),
    setLoading,
  );
};

const sumValueByField = (fieldName, data) => {
  return data.reduce((a, b) => parseInt(b[fieldName]) + a, 0);
};
